import { 
  Checkbox,
  ColumnActionsMode,
  DefaultButton, 
  Dialog, 
  DialogFooter, 
  DialogType, 
  Dropdown, 
  IColumn, 
  IconButton, 
  IDropdownOption, 
  MessageBarType, 
  PrimaryButton, 
  Text,
  TextField,
  Toggle,
} from '@fluentui/react';
import classNames from 'classnames';
import { FC, FormEvent, ReactElement, useEffect, useState } from 'react';
import SeparatorGy from '../../../../../../SeparatorGy/SeparatorGy';
import styles from './TransferTiresReconciliation.module.scss';
import apiService from '../../../../../../../api';
import useNotifications from '../../../../../../../hooks/useNotifications';
import { customerSelector, locationSelector } from '../../../../../../../redux/recordKeepingSlice';
import { useSelector } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import { IPaginationProps } from '../../../../../../Pagination/IPaginationProps';
import { pageSizes } from '../../../../../../../consts/recordKeeping';
import LoadingScreen from '../../../../../../LoadingScreen/LoadingScreen';
import { ITransferTiresReconciliationState } from './ITransferTiresReconciliationState';
import AutoCompleteField from '../../../../../../Common/Search/AutoCompleteField';
import Tooltip from '../../../../../../../shared/Tooltip';
import { CUSTOMER, LOCATION, TransferTireDetailsCols, emptyTransferTires, transferTo } from './const';
import Pagination from '../../../../../../Pagination/Pagination';
import moment from 'moment';
import { ITransferTiresReconciliationProps } from './ITransfterTiresReconciliationProps';
import { ITransferReconciliationTire } from './TransferReconciliationTire';
import { AVAILABLE, LOCKED, TRASFER_TIRES_CODE, TRASFER_TIRES_ID } from '../../../../../../../consts/modulesCodes';
import { userSelector } from '../../../../../../../redux/userSlice';
import { downloadFile, printingTypes } from '../../../../../../PrintingModal/consts';
import PrintingModal from '../../../../../../PrintingModal/PrintingModal';
import { sortOrder } from '../../../../../../../consts/sortOrder';

const TransferTiresReconciliation: FC<ITransferTiresReconciliationProps> = ({
  surveyDetails: {
    docNum,
    tireOptixSurveyId,
    glsSurveyId,
    comments,
    dispCode,
    surveyDate,
  },
  shippingDetails: {
    shipToCustomerName,
    shipToLocCode,
    shipToCustomerId,
    shipToLocId,
  },
  fetchQueueRecords,
  tireDetails: {
    tireId,
  },
}): ReactElement => {

  const [state, setState] = useState<ITransferTiresReconciliationState>({
    items: [],
    selectedItems: [],
    loading: false,
    foundCount: 0,
  });
  const [columnsState, setColumnsState] = useState<Array<any>>(TransferTireDetailsCols);

  const { addNotification } = useNotifications();
  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({ key: pageSizes[1], text: pageSizes[1].toString() });
  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) => setPaginationProps((prev: any) => ({ ...prev, current: newPage })),
  });

  const handleSelect = (e: any, itemId: any) => {
    if (e.target.checked) {
      setState((prev: any) => ({ ...prev, selectedItems: [...state.selectedItems, itemId] }));
    } else {
      setState((prev: any) => ({ ...prev, selectedItems: state.selectedItems.filter(row => row !== itemId) }));
    }
  };

  const handleSelectAll = (e: any, items: Array<any>) => {
    e.target.checked ? setState((prev: any) => ({ ...prev, selectedItems: items })) : setState((prev: any) => ({ ...prev, selectedItems: [] }));
  };

  const onChangeCountOnPage = (event: FormEvent<HTMLDivElement>, item: IDropdownOption<any> | undefined): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(({ isSorted }) => isSorted);
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  const actionsClassName = (item: any) => {
    if (item.isSorted && item.isSortedDescending){
      return styles.descending;
    } else if (item.isSorted && !item.isSortedDescending){
      return styles.ascending;
    } else {
      return undefined;
    }
  };

  const onColumnClick = (column: IColumn): void => {
    if (column.columnActionsMode == ColumnActionsMode.disabled) return;
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    setColumnsState(newColumns);
  };

  const { id: customerId, customerName, prefix } = useSelector(customerSelector);
  const { id: locationId, locationCode } = useSelector(locationSelector);
  const [newComments, setNewComments] = useState(comments);
  const [selectedKeyTransferTo, setSelectedKeyTransferTo] = useState<string | undefined>(transferTo[0].key);
  const [tireToBeAdded, setTireToBeAdded] = useState<any>(emptyTransferTires);
  const [isRelocateTiresChecked, setIsRelocateTiresChecked] = useState(false);

  const [hideAddDialog, { toggle: toggleAddDialog }] = useBoolean(true);
  const [transferToCustomer, setTransferToCustomer] = useState(false);
  const [locked, setLocked] = useState<any>({});
  const { email: userEmail, displayName: userName } = useSelector(userSelector);
  const [showPrintExport, { toggle: toggleShowPrintExport }] = useBoolean(false);
  const [viewErrors, setViewErrors] = useState<boolean>(false);

  const DATE_FORMAT = 'MM/DD/YYYY';
  const formatDate = (date: string) => date ? moment(date).format(DATE_FORMAT) : null;

  const fetchTireDetails = async () => {
    setState(prev => ({...prev, loading:true}));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.tireDispositionQueueTires.get(
        customerId,
        locationId,
        tireOptixSurveyId ? Number.parseInt(tireOptixSurveyId, 10) : null,
        glsSurveyId,
        viewErrors,
        sortOrder,
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
      );
      const foundCount = data.total.found;
      const items = data.data.map((item:any) => ({...item, dateModified: formatDate(item.dateModified)}));
      if (tireId) {
        setState((prev: any) => ({...prev, selectedItems: [tireId] }));
      }
      else {
        setState((prev: any) => ({...prev, selectedItems: [] }));
      }
      setState((prev: any) => ({ ...prev, items, foundCount }));
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
    } catch (e: any) {
      addNotification({
        text: `Transfer Reconciliation Tires fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const handleTransfer = async () => {
    setState(prev => ({...prev, loading:true}));
    if(selectedKeyTransferTo === CUSTOMER && isRelocateTiresChecked === false) {
      toggleAddDialog();
    }
    try {
      saveComments();
      const listOfTires = state.items.filter(item => state.selectedItems.includes(item.tireOptixNo) && !isStatusComplete(item)).map(item => ({
        tireOptixNo: item.tireOptixNo,
        prefix: item.prefix,
        brandNo: item.brandNo,
        suffix: item.suffix,
      }));
      const transferTiresData = [{
        customerId: customerId,
        locationId: locationId, 
        newCustomerId: shipToCustomerId,
        newLocationId: shipToLocId,
      }];
      await apiService.tireDispositionQueueTires.transfer(transferTiresData, glsSurveyId, listOfTires, comments,tireToBeAdded.relocateTires, transferToCustomer, true);
      addNotification({
        text: 'Tires were successfully submitted for Transfer.',
        type: MessageBarType.success,
      });
    }
    catch (e:any) {
      const { response } = e;
      setState(prev => ({ ...prev, loading: false }));
      switch (response.status) {
        case 400:
          return addNotification({
            text: 'Submitting tires error: '+ response.data.state[0]?.message,
            type: MessageBarType.error,
          });
        default:
          return addNotification({
            text: response.data?.message,
            type: MessageBarType.error,
          });
      }
    } finally {
      if (paginationProps.current === 1) {
        await fetchTireDetails();
      } else {
        await paginationProps.onChangePage(1);
      }
      fetchQueueRecords();
    }
  };

  const handlePrint = async (printingType: any) => {
    setState(prev => ({ ...prev, loading: true }));
    toggleShowPrintExport();
    try {
      const tireOptixSurveyId = docNum ? Number.parseInt(docNum, 10) : null;
      const requestData = {
        customerId,
        locationId,
        tireOptixSurveyId,
        glsSurveyId,
        dispCode,
        viewErrors,
        pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        shipToCustomerName,
        shipToLocCode,
      };
      const { data }: any = printingType === printingTypes.excel ?
        await apiService.tireDispositionQueueTires.printExcel(requestData) :
        await apiService.tireDispositionQueueTires.printPdf(requestData);

      addNotification({
        text: 'File was successfully received.',
        type: MessageBarType.success,
      });
      downloadFile(data, printingType);
    } catch (e: any) {
      console.log(e);
      addNotification({
        text: 'Printing error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const updateComments = (event:any) => {
    setNewComments(event.target.value);
  };

  const saveComments = () => {
    if (comments !== newComments) {
      comments = newComments;
      setNewComments(comments);
    } 
  };

  const submitComments = async () => {
    setState(prev => ({...prev, loading:true}));
    try {
      saveComments();
      await apiService.tireDispositionQueue.saveComments(
        comments,
        customerId,
        locationId,
        glsSurveyId,
        tireOptixSurveyId,
      );
      addNotification({
        text: 'Comments were successfully updated.',
        type: MessageBarType.success,
      });
    } catch (e: any) {
      addNotification({
        text: 'Comments updating error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      fetchQueueRecords();
    }
  };

  const onChangeField = (field: any, value: any) => {
    setTireToBeAdded((prev: any) => ({ ...prev, [field]: value }));
    if (field === 'relocateTires') {
      setIsRelocateTiresChecked(value);
    }
  };

  const setTireToBeEdited = (id: string, field: string, value: any, regExp?: RegExp) => {
    if (!regExp || (regExp?.test(value))) {
      setState((prev: any) => ({
        ...prev,
        items: prev.items.map((item: ITransferReconciliationTire) => item.id === id ?  { ...item, [field]: value } : item),
      }));
    }
    if (field === 'prefix') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.prefix = value);
    }
    else if (field === 'brandNo') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.brandNo = value);
    }
    else if (field === 'suffix') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.suffix = value);
    }
  };

  const isStatusComplete = (item: any) => {
    if (item.status === 'Completed')
      return true;
  };
  const isStatusError = (item: any) => {
    if (item.status === 'Error')
      return true;
  };

  const isPrefixMatching = (item: any) => {
    if (item.prefix !== item.ogPrefix && 
      !(item.prefix == null && item.ogPrefix === '') && 
      !(item.prefix === '' && item.ogPrefix == null)) {
      return true;
    } else {  
      return false;
    }
  };

  const isBrandMatching = (item: any) => {
    if (item.brandNo !== item.ogBrandNo && 
      !(item.brandNo == null && item.ogBrandNo === '') && 
      !(item.brandNo === '' && item.ogBrandNo == null)) {
      return true;
    } else {  
      return false;
    }
  };

  const isSuffixMatching = (item: any) => {
    if (item.suffix !== item.ogSuffix && 
      !(item.suffix == null && item.ogSuffix === '') && 
      !(item.suffix === '' && item.ogSuffix == null)) {
      return true;
    } else {  
      return false;
    }
  };

  const blockAPI = async (status: string) => {
    try {
      const { data } = await apiService.blockAPI(TRASFER_TIRES_ID, TRASFER_TIRES_CODE, userEmail, userName, status, customerId, locationId);
      setLocked(data);
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Customers fetching error: ${response?.data?.message}`,
        type: MessageBarType.error,
      });
    }
  };

  useEffect(() => {
    blockAPI(LOCKED);
    return () => {
      blockAPI(AVAILABLE);
    };
  }, []);

  useEffect(() => {
    if (customerId === shipToCustomerId){
      setTransferToCustomer(false);
      setSelectedKeyTransferTo(LOCATION);
    }
    else {
      setTransferToCustomer(true);
      setSelectedKeyTransferTo(CUSTOMER);
    }
  }
  , [customerId, shipToCustomerId]);

  useEffect(() => {
    fetchTireDetails();
  }, [
    paginationProps.current,
    countOnPage,
    columnsState,
    customerId,
    locationId,
    tireOptixSurveyId,
    glsSurveyId,
    viewErrors,
  ]);

  return (
    <>
      { !locked.isLocked ? 
        <div>
          <div className={styles.customerInfo}>
            <div className="ms-Grid">
              <div className="ms-Grid-row">
                <div className="ms-Grid-col">
                  <AutoCompleteField
                    initialValue={prefix}
                    list={[]}
                    label="Customer Prefix"
                    data-testid='customer-prefix'
                    icon="FavoriteStar"
                    disabled
                  />
                  <AutoCompleteField
                    initialValue={customerName}
                    list={[]}
                    label="Customer Name"
                    data-testid='customer-name'
                    icon="Contact"
                    disabled
                  />
                  <AutoCompleteField
                    initialValue={locationCode}
                    list={[]}
                    label="Location Code"
                    data-testid='location-code'
                    icon="MapPin"
                    disabled
                  />
                </div>
                <div className="ms-Grid-col">
                  <AutoCompleteField
                    initialValue={docNum}
                    list={[]}
                    label="Tire Optix Survey ID"
                    data-testid='to-survey-id'
                    icon="FavoriteStar"
                    disabled
                  />
                  <AutoCompleteField
                    initialValue={glsSurveyId}
                    list={[]}
                    label="GLS Survey ID"
                    data-testid='gls-survey-id'
                    icon="FavoriteStar"
                    disabled
                  />
                  <AutoCompleteField
                    initialValue={surveyDate}
                    list={[]}
                    label="Survey Date"
                    data-testid='survey-date'
                    icon="DateTime"
                    disabled
                  />
                </div>
                <div className="ms-Grid-col">
                  <AutoCompleteField
                    initialValue={shipToCustomerName}
                    list={[]}
                    label="Ship To Customer Name"
                    data-testid='ship-to-customer-name'
                    icon="DeliveryTruck"
                    disabled
                  />
                  <AutoCompleteField
                    initialValue={shipToLocCode}
                    list={[]}
                    label="Ship To Location Code"
                    data-testid='ship-to-location-code'
                    icon="DeliveryTruck"
                    disabled
                  />
                </div>
                <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                  <TextField
                    value={newComments}
                    label="Comments"
                    data-testid='comments'
                    multiline rows={8}
                    onChange={updateComments}
                  />
                </div>
                <div className='ms-Grid-col'>
                  <div className={classNames(styles.lastColumn)}>
                    <DefaultButton
                      id="saveComments"
                      text="Save Comments"
                      onClick={submitComments}
                    />
                    <Checkbox
                      label="Relocate Tires"
                      checked={tireToBeAdded.relocateTires}
                      onChange={(e, relocateTires) => onChangeField('relocateTires', relocateTires)}
                    />
                    <Toggle
                      label="Show Only Errors"
                      checked={viewErrors}
                      onChange={() => setViewErrors(!viewErrors)}
                    />
                  </div>
                </div>
                <div className={classNames(styles.columns, 'ms-Grid-col', 'ms-sm1')}>
                  <Dialog
                    hidden={hideAddDialog}
                    onDismiss={toggleAddDialog}
                    dialogContentProps={{
                      type: DialogType.normal,
                      title: 'Confirmation',
                      subText: 'WARNING- you are about to change the Tire Class to 4 (Used Tire Transfer). Click OK to proceed.',
                    }}
                    modalProps={{ isBlocking: true }}
                  >
                    <DialogFooter>
                      <PrimaryButton id="addButton" onClick={() => handleTransfer()} text="OK" />
                      <DefaultButton onClick={toggleAddDialog} text="Cancel" />
                    </DialogFooter>
                  </Dialog>
                </div>

              </div>

            </div>
          </div>
          <div className={styles.tableHeading}>
            <div>
              <Text variant="xLarge" className={styles.highlight}>Tires</Text>
              <SeparatorGy vertical />
              <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
            </div>
            <div className={styles.buttonsWrapper}>
              <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
              <Dropdown
                options={pageSizes.map(pageSize => ({
                  key: pageSize,
                  text: pageSize.toString(),
                }))}
                defaultSelectedKey={pageSizes[0]}
                selectedKey={countOnPage?.key}
                onChange={onChangeCountOnPage}
              />
              <DefaultButton
                id="export"
                text="Export"
                onClick={toggleShowPrintExport}
              />
              <IconButton
                id="refreshButton"
                iconProps={{ iconName: 'Refresh' }} 
                onClick={fetchTireDetails} 
              />
            </div>
          </div>
          <div className={styles['table-wrapper']}>
            <table className={styles['oos-table']}>
              <thead>
                <tr>
                  {
                    columnsState.map(item => (
                      <th
                        key={item.key}
                        className={
                          actionsClassName(item)
                        }
                        onClick={() => onColumnClick(item)}
                      >
                        {item.name}
                        {item.tooltip ? <Tooltip 
                          content={item.tooltip}
                        />:null}
                      </th>
                    ))
                  }
                  <th>
                    { state.items.filter(item => !isStatusComplete(item)).length > 0 ? 
                      <div className={styles.round}>
                        <input
                          type="checkbox"
                          id="allTires"
                          checked={state.items.length !== 0 && (state.items.filter(item => !isStatusComplete(item)).length == state.selectedItems.length)}
                          onChange={(e) => handleSelectAll(e, state.items.filter(item => !isStatusComplete(item)).map(({ tireOptixNo }) => tireOptixNo))}
                        />
                        <label htmlFor="allTires">{''}</label>
                      </div> : null }
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  state.items.map(item => (
                    <tr key={item.tireOptixNo}
                      className={
                        classNames(
                          styles.trBasic,
                          {
                            [styles.trSelected]: state.selectedItems.includes(item.tireOptixNo),
                          },
                          {
                            [styles.trError]: isStatusError(item),
                          },
                          {
                            [styles.trComplete]: isStatusComplete(item),
                          },
                        )
                      }
                    >
                      <td>
                        <TextField
                          defaultValue={item.prefix}
                          onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'prefix', a)}
                          id={item.tireOptixNo+'_prefix'}
                          disabled = {isStatusComplete(item)}
                          styles={{
                            fieldGroup: { 
                              background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                            },
                          }}
                        />
                        {isPrefixMatching(item) ? 
                          <Tooltip content={'Tire Optix: '+item.ogPrefix}/> : null
                        }
                      </td>
                      <td>
                        <TextField
                          defaultValue={item.brandNo}
                          onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'brandNo', a)}
                          id={item.tireOptixNo+'_brandNo'}
                          disabled = {isStatusComplete(item)}
                          styles={{
                            fieldGroup: { 
                              background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                            },
                          }}
                        />
                        {isBrandMatching(item) ? 
                          <Tooltip content={'Tire Optix: '+item.ogBrandNo}/> : null
                        }
                      </td>
                      <td>
                        <TextField
                          defaultValue={item.suffix}
                          onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'suffix', a)}
                          id={item.tireOptixNo+'_suffix'}
                          disabled = {isStatusComplete(item)}
                          styles={{
                            fieldGroup: { 
                              background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                            },
                          }}
                        />
                        {isSuffixMatching(item) ? 
                          <Tooltip content={'Tire Optix: '+item.ogSuffix}/> : null
                        }
                      </td>
                      <td>{item.tireSize}</td>
                      <td>{item.dispCode}</td>
                      <td>{item.status} {(isStatusError(item) && item.errMsg) ? <Tooltip 
                        content={item.errMsg}
                      />:null}</td>
                      <td>{item.vehicleNo}</td>
                      <td>{item.glsWhlPos}</td>
                      <td>{item.dateModified}</td>
                      <td>{item.prevVehicleNo}</td>
                      <td>{item.prevWheelPos}</td>
                      <td>{item.totMiles}</td>
                      <td>{item.comments}</td>
                      <td>{item.images ? <a href={item.images} target='_blank'>View</a> : null}</td>
                      <td>{item.lastModifiedBy}</td>
                      <td>
                        {!isStatusComplete(item) ?
                          <div className={styles.round}>
                            <input type="checkbox" id={item.tireOptixNo} checked={state.selectedItems.includes(item.tireOptixNo)} onChange={(e) => handleSelect(e, item.tireOptixNo)} />
                            <label htmlFor={item.tireOptixNo}>{''}</label>
                          </div> : null}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          <div className={styles.detailsRow}>
            <div className={styles.detailsColumn}>
              <div className={styles.fieldsActions}>
                <DefaultButton
                  id="transfer"
                  text="Transfer"
                  onClick={selectedKeyTransferTo === CUSTOMER && isRelocateTiresChecked === false ? toggleAddDialog : () => handleTransfer()}
                  disabled={state.selectedItems.length === 0} 
                />
              </div>
            </div>
          </div>
        </div>
        : <>
          <div className={styles.columns}>
            <Text variant='xxLarge' className={styles.highlight}>{locked?.lockedBy}</Text>
          </div>
          <div className={styles.columns}>
            <Text variant='xLarge' className={styles.highlight}>{locked?.message}</Text>
          </div>
        </>
      }
      {state.loading && <LoadingScreen />}
      <Pagination {...paginationProps} />
      <PrintingModal
        isOpened={showPrintExport}
        onClose={toggleShowPrintExport}
        onPrint={handlePrint}
      />
    </>
  );
};

export default TransferTiresReconciliation;